import {
  clearPerformanceOrgCache,
  clearSiteHealthOrgCache,
} from '@vwam/tracker-ui'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { queryClient } from '@/lib/queryClient'
import type {
  AuthError,
  AuthState,
  AuthStore,
  ChangePasswordRequest,
  LoginRequest,
  Organization,
  Role,
  TokenData,
  UpdateProfileRequest,
  User,
} from './types'

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      organization: null,
      role: null,
      permissions: [],
      tokens: {
        accessToken: null,
        refreshToken: null,
        expiresAt: null,
      },
      authState: 'idle',
      error: null,
      isLoading: false,

      // Actions
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null, authState: 'loading' })

        try {
          // Import auth service dynamically to avoid circular dependencies
          const { authService } = await import('@/services/auth')

          const response = await authService.login(credentials)

          const tokens: TokenData = {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresAt: Date.now() + response.expiresIn * 1000,
          }

          set({
            user: response.user,
            organization: response.organization,
            role: response.role,
            permissions: response.permissions ?? [],
            tokens,
            authState: 'authenticated',
            error: null,
            isLoading: false,
          })

          try {
            const freshUser = await authService.getCurrentUser()

            set({ user: freshUser })
          } catch {
            // If fetching fresh user fails, continue with login response data
            // The login was still successful
          }
        } catch (error) {
          const err = error as { message?: string }
          const authError: AuthError = {
            message: err?.message || 'Login failed',
            code: 'LOGIN_ERROR',
          }

          set({
            error: authError,
            authState: 'error',
            isLoading: false,
          })
          throw error
        }
      },

      googleLogin: async (credential: string) => {
        set({ isLoading: true, error: null, authState: 'loading' })

        try {
          const { authService } = await import('@/services/auth')

          const response = await authService.googleSignIn(credential)

          const tokens: TokenData = {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresAt: Date.now() + response.expiresIn * 1000,
          }

          set({
            user: response.user,
            organization: response.organization ?? null,
            role: response.role ?? null,
            permissions: response.permissions ?? [],
            tokens,
            authState: 'authenticated',
            error: null,
            isLoading: false,
          })
        } catch (error) {
          const err = error as { message?: string }
          const authError: AuthError = {
            message: err?.message || 'Google Sign-In failed',
            code: 'GOOGLE_LOGIN_ERROR',
          }

          set({
            error: authError,
            authState: 'error',
            isLoading: false,
          })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })

        try {
          // Import auth service dynamically to avoid circular dependencies
          const { authService } = await import('@/services/auth')

          // Call logout endpoint (will fail gracefully if network is down)
          await authService.logout()
        } catch (error) {
          // Don't throw error on logout - we still want to clear local state
          console.warn('Logout request failed:', error)
        } finally {
          // Always clear local state regardless of API call success
          set({
            user: null,
            organization: null,
            role: null,
            permissions: [],
            tokens: {
              accessToken: null,
              refreshToken: null,
              expiresAt: null,
            },
            authState: 'unauthenticated',
            error: null,
            isLoading: false,
          })
        }
      },

      refreshToken: async () => {
        const { tokens, organization } = get()

        if (!tokens.refreshToken) {
          set({ authState: 'unauthenticated' })

          return false
        }

        try {
          set({ isLoading: true })

          // Import auth service dynamically to avoid circular dependencies
          const { authService } = await import('@/services/auth')

          const response = await authService.refreshToken(
            tokens.refreshToken,
            organization?.id
          )

          const newTokens: TokenData = {
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresAt: Date.now() + response.expiresIn * 1000,
          }

          set({
            tokens: newTokens,
            authState: 'authenticated',
            isLoading: false,
            error: null,
          })

          // Resync user, organization, role, and permissions from the backend
          // to ensure store stays in sync with the JWT
          try {
            const authContext = await authService.getAuthContext()
            set({
              user: authContext.user,
              organization: authContext.organization,
              role: authContext.role,
              permissions: authContext.permissions,
            })
          } catch {
            // Non-fatal: tokens are refreshed, just couldn't resync context
            console.warn('Failed to resync auth context after token refresh')
          }

          return true
        } catch (error) {
          console.error('Token refresh failed:', error)

          set({
            authState: 'unauthenticated',
            isLoading: false,
            tokens: {
              accessToken: null,
              refreshToken: null,
              expiresAt: null,
            },
          })

          return false
        }
      },

      updateProfile: async (data: UpdateProfileRequest) => {
        set({ isLoading: true, error: null })

        try {
          // Import auth service dynamically to avoid circular dependencies
          const { authService } = await import('@/services/auth')

          const updatedUser = await authService.updateProfile(data)

          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const err = error as { message?: string }
          const authError: AuthError = {
            message: err?.message || 'Profile update failed',
            code: 'PROFILE_UPDATE_ERROR',
          }

          set({ error: authError, isLoading: false })

          throw error
        }
      },

      changePassword: async (data: ChangePasswordRequest) => {
        set({ isLoading: true, error: null })

        try {
          // Import auth service dynamically to avoid circular dependencies
          const { authService } = await import('@/services/auth')

          await authService.changePassword(data)

          set({
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const err = error as { message?: string }
          const authError: AuthError = {
            message: err?.message || 'Password change failed',
            code: 'PASSWORD_CHANGE_ERROR',
          }

          set({ error: authError, isLoading: false })

          throw error
        }
      },

      switchOrganization: async (organizationId: string) => {
        const { organization: currentOrg } = get()

        set({ isLoading: true, error: null })

        try {
          // Import auth service and queryClient dynamically
          const { authService } = await import('@/services/auth')

          // Call API to switch workspace
          const response = await authService.switchOrganization({
            organizationId,
          })

          // Drop previous-org tracker caches before the atomic tenant swap.
          if (currentOrg?.id) {
            await Promise.all([
              clearPerformanceOrgCache(queryClient, currentOrg.id),
              clearSiteHealthOrgCache(queryClient, currentOrg.id),
            ])
          }

          // The switch endpoint rotates only the access token. Apply the full
          // tenant context together while preserving the current refresh token.
          set((state) => ({
            tokens: {
              accessToken: response.accessToken,
              refreshToken: state.tokens.refreshToken,
              expiresAt: Date.now() + response.expiresIn * 1000,
            },
            organization: response.organization,
            role: response.role,
            permissions: response.permissions,
            isLoading: false,
            error: null,
          }))

          // Optional: Clean up old organization's cached queries
          // React Query will automatically refetch for new org because query keys include orgId
          if (currentOrg?.id) {
            queryClient.removeQueries({
              queryKey: ['configurations', currentOrg.id],
            })
          }

          return response
        } catch (error) {
          const err = error as { message?: string }
          const authError: AuthError = {
            message: err?.message || 'Failed to switch workspace',
            code: 'WORKSPACE_SWITCH_ERROR',
          }

          set({ error: authError, isLoading: false })

          throw error
        }
      },

      // Utility actions
      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setUser: (user: User | null) => set({ user }),
      setOrganization: (organization: Organization | null) =>
        set({ organization }),
      setRole: (role: Role | null) => set({ role }),
      setPermissions: (permissions: string[]) => set({ permissions }),
      setTokens: (tokens: TokenData) => set({ tokens }),
      setAuthState: (authState: AuthState) => set({ authState }),
      setError: (error: AuthError | null) => set({ error }),

      // Computed getters
      isAuthenticated: () => {
        const { user, tokens, authState } = get()

        return !!(user && tokens.accessToken && authState === 'authenticated')
      },

      isTokenExpired: () => {
        const { tokens } = get()

        if (!tokens.expiresAt) return true

        return Date.now() >= tokens.expiresAt
      },

      getAccessToken: () => {
        const { tokens, isTokenExpired } = get()

        if (!tokens.accessToken || isTokenExpired()) return null

        return tokens.accessToken
      },

      getUserFullName: () => {
        const { user } = get()

        if (!user) return ''

        return user.fullName || `${user.firstName} ${user.lastName}`.trim()
      },

      isOnboardingCompleted: () => {
        const { user } = get()
        return !!user?.onboardingCompletedAt
      },

      hasPermission: (permission: string) => {
        const { permissions } = get()
        return Array.isArray(permissions) && permissions.includes(permission)
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        organization: state.organization,
        role: state.role,
        permissions: state.permissions,
        tokens: state.tokens,
        authState: state.authState,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return

        // Validate token expiration on rehydration
        const { tokens } = state

        if (tokens?.expiresAt && Date.now() >= tokens.expiresAt) {
          // Token is expired, clear auth state
          // We can't call logout() here as it's async, so we clear state directly
          useAuthStore.setState({
            user: null,
            organization: null,
            role: null,
            permissions: [],
            tokens: {
              accessToken: null,
              refreshToken: null,
              expiresAt: null,
            },
            authState: 'unauthenticated',
            error: null,
            isLoading: false,
          })
        }
      },
    }
  )
)
